import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData, type QueryClient } from '@tanstack/react-query';
import { invalidateCarbonCaches } from '@/lib/carbonCacheKeys';
import { invalidateAuditCaches } from '@/lib/auditCacheKeys';
import type { TFunction } from 'i18next';
import { EXPENSE_BATCH_MAX_IDS, type ExpenseBatchDeleteResult } from '@releve/shared';
import type {
  ExpenseWire,
  ExpenseLineItemWire,
  ExpenseIndexRowWire,
  ExpenseDetailResponse,
  ExpensePickerResponse,
  ExpenseLabelsResponse,
  ExpenseLineItemsResponse,
  ExpenseFirstLineItemResponse,
  ExpensePeriodTotalsResponse,
  NlWkrCategory,
} from '@releve/shared';
import { toast } from '@/hooks/useToast';
import { toastTaxRateRefusal } from '@/lib/taxRateRefusal';
import { useTranslation } from '@/hooks/useTranslation';
import { indexQueryOptions } from '@/hooks/lib/indexQueryOptions';
import {
  authedGet,
  authedPost,
  authedPatch,
  authedDelete,
  vatDeclaredOf,
  VAT_DECLARED_MUTATION_META,
} from '@/lib/api';
import { formatLocalISODate } from '@/lib/dateUtils';
import type { TaxTreatment } from '@/lib/taxCalculations';
import {
  BULK_DELETE_NOT_ATTEMPTED,
  BULK_DELETE_REQUEST_FAILED,
  formatVatDeclaredMessage,
  useBulkDelete,
  type BulkDeleteConfig,
  type BulkDeleteOutcome,
  type RunBulkDeleteOptions,
} from '@/hooks/useBulkDelete';
import { trackEvent } from '@/lib/posthog';
import { invalidateProjectMoney } from "@/hooks/useProjectMoneySummary";

/**
 * The read hooks below go through the Hono backend (plain RLS-scoped reads,
 * see docs/BACKEND_API.md → "Read endpoints" and
 * @releve/shared/schemas/expensesReads). The wire types are the source of
 * truth; these web-facing aliases narrow `tax_treatment` to the web
 * TaxTreatment union so hook consumers keep the exact pre-migration shapes.
 */
export interface ExpenseLineItem extends Omit<ExpenseLineItemWire, 'tax_treatment'> {
  tax_treatment?: TaxTreatment;
}

export interface Expense extends Omit<ExpenseWire, 'line_items'> {
  line_items?: ExpenseLineItem[];
}

export interface ExpenseMutationResult {
  success: boolean;
  expense_id: string;
  ledger_transaction_id: string;
  error?: string;
}

export interface CreateExpenseData {
  business_id?: string;
  person_id?: string;
  custom_business_name?: string;
  expense_date: string;
  description: string;
  payment_account_id: string;
  reference?: string;
  project_id?: string;
  fund_id?: string;
  line_items: Array<{
    expense_account_id: string;
    description: string;
    quantity: number;
    unit_price: number;
    tax_rate: number;
    tax_rate_id?: string;
    sort_order: number;
    tax_treatment?: string;
    tax_inclusive?: boolean;
    exemption_reason?: string;
    exemption_reason_other?: string;
    /** NL WKR classification (jurisdiction-gated in the UI). */
    wkr_category?: NlWkrCategory;
  }>;
}

/** One row of the slim expense index (the `expense_index` DB view). */
export type ExpenseIndexRow = ExpenseIndexRowWire;

/**
 * The COMPLETE slim expense index for an org — the API pages the
 * `expense_index` view past PostgREST's 1,000-row cap, so it is never
 * silently truncated. Powers the expenses grid's client-side
 * search/sort/filter; heavy detail (line items, ledger entries) is fetched
 * lazily per row via `useExpenseById`.
 *
 * Liveness: the key sits under the `['expenses', orgId]` prefix, so the
 * expense mutations in this file (and the clearing/import/asset/carbon
 * flows) refresh it via their existing `invalidateQueries`. The
 * ledger-transaction mutations in useLedgerTransactions.ts also invalidate
 * this prefix, since the index carries transaction-sourced fields
 * (reference, touches_bank, import links). `staleTime` only suppresses
 * redundant mount/focus refetches between mutations.
 */
export const useExpenseIndex = (
  organizationId?: string,
  options?: { projectId?: string; businessId?: string; personId?: string },
) => {
  // Normalized 3-key shape — a stable key object prevents cache
  // fragmentation between surfaces that omit different options.
  const normalized = {
    projectId: options?.projectId ?? null,
    businessId: options?.businessId ?? null,
    personId: options?.personId ?? null,
  };
  // Key shape, enabled gate, {items} unwrap, and the same-org placeholder
  // guard (no cross-org flash) all live in indexQueryOptions.
  return useQuery(
    indexQueryOptions<ExpenseIndexRowWire>({
      entity: 'expenses',
      organizationId,
      path: `/api/v1/expenses/orgs/${organizationId}/expenses/index`,
      params: normalized,
    }),
  );
};

/**
 * Lightweight expense list for pickers (materials grid expense cell, etc).
 * Skips line items / asset counts / carbon joins that `useExpenses` pulls —
 * keeps first-byte fast for a dropdown.
 *
 * - Idle list: most recent `limit` by `expense_date desc`.
 * - Search list: when `search` has ≥2 chars, server-side `ilike` on
 *   `expense_number` OR `description` (debounce in the caller).
 */
export interface ExpenseListItem {
  id: string;
  expense_number: string;
  expense_date: string;
  description: string;
  total_amount: number;
  business_name: string | null;
}

export const useExpensesList = (
  organizationId: string | undefined,
  options?: { search?: string; limit?: number },
) => {
  const search = options?.search?.trim() ?? '';
  const limit = options?.limit ?? 50;
  const useSearch = search.length >= 2;

  return useQuery({
    // orgId in slot 1 (matching the index hook's ['expenses', orgId, 'index',
    // ...] convention) so the org-scoped ['expenses', orgId] mutation
    // invalidations refresh pickers too.
    queryKey: ['expenses', organizationId, 'picker', useSearch ? search : '', limit],
    queryFn: async (): Promise<ExpenseListItem[]> => {
      if (!organizationId) return [];

      // Search-term sanitizing (ilike wildcards + .or() grammar chars) happens
      // server-side in the picker handler.
      const params = new URLSearchParams({ limit: String(limit) });
      if (useSearch) params.set('search', search);

      const result = await authedGet<ExpensePickerResponse>(
        `/api/v1/expenses/orgs/${organizationId}/expenses/picker?${params.toString()}`,
      );
      return result.items;
    },
    enabled: !!organizationId,
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Spend on the P&L basis (every Expenses-category account — expenses, AP
 * bills and journals alike) for an inclusive date window, plus the same
 * window one year earlier. The API clamps `to` at the org's today, so the
 * response's `window` may end before the requested `to`.
 *
 * Liveness: the key sits under `['accountBalances', orgId]`, which the
 * expense, journal and AP mutations already invalidate — no extra
 * invalidation code. Disabled until both dates are set.
 */
export const useExpensePeriodTotals = (
  organizationId: string | undefined,
  range: { from?: Date | null; to?: Date | null; projectId?: string | null },
) => {
  const from = formatLocalISODate(range.from ?? null);
  const to = formatLocalISODate(range.to ?? null);
  const projectId = range.projectId ?? null;

  return useQuery({
    queryKey: ['accountBalances', organizationId, 'expensePeriodTotals', from, to, projectId],
    queryFn: async (): Promise<ExpensePeriodTotalsResponse> => {
      const params = new URLSearchParams({ from: from!, to: to! });
      if (projectId) params.set('projectId', projectId);
      return authedGet<ExpensePeriodTotalsResponse>(
        `/api/v1/expenses/orgs/${organizationId}/expenses/period-totals?${params.toString()}`,
      );
    },
    enabled: !!organizationId && !!from && !!to,
    staleTime: 2 * 60 * 1000,
    // Keep the previous window on screen while a new range loads — but never
    // across an org switch (no cross-org flash).
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === organizationId ? keepPreviousData(previous) : undefined,
  });
};

/**
 * Minimal expense + line-item label lookup for the materials grid cell
 * renderer. Fetches just `expense_number` and `line_item.description` for
 * a pre-known set of ids. Avoids the 200-row over-fetch the reviewer
 * flagged — we only pull what's actually linked to the rendered rows.
 */
export interface ExpenseRowLabels {
  expenses: Map<string, string>;
  lineItems: Map<string, string>;
}

/**
 * Max ids per id-list per labels request. The endpoint schema hard-rejects
 * >500 uuids per list, and ~430 uuids already brushes URL-length limits —
 * stay well under both and fan out in parallel batches instead.
 */
const LABELS_BATCH_SIZE = 150;

const chunkIds = (ids: string[]): string[][] => {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += LABELS_BATCH_SIZE) {
    chunks.push(ids.slice(i, i + LABELS_BATCH_SIZE));
  }
  return chunks;
};

export const useExpenseLabels = (
  organizationId: string | undefined,
  expenseIds: string[],
  lineItemIds: string[] = [],
) => {
  // Stable join keys — order the ids so the query key doesn't flap on
  // reference changes.
  const keyExpenseIds = [...expenseIds].sort();
  const keyLineItemIds = [...lineItemIds].sort();
  const hasWork = keyExpenseIds.length > 0 || keyLineItemIds.length > 0;

  return useQuery({
    queryKey: ['expenses', 'labels', organizationId, keyExpenseIds, keyLineItemIds],
    queryFn: async (): Promise<ExpenseRowLabels> => {
      const result: ExpenseRowLabels = {
        expenses: new Map(),
        lineItems: new Map(),
      };
      if (!organizationId || !hasWork) return result;

      // Batch both id lists in lockstep — request i carries chunk i of each
      // list (a lone request when both fit), all fired in parallel and merged.
      const expenseChunks = chunkIds(keyExpenseIds);
      const lineItemChunks = chunkIds(keyLineItemIds);
      const batchCount = Math.max(expenseChunks.length, lineItemChunks.length);

      const responses = await Promise.all(
        Array.from({ length: batchCount }, (_, i) => {
          const params = new URLSearchParams();
          const expenseBatch = expenseChunks[i];
          const lineItemBatch = lineItemChunks[i];
          if (expenseBatch?.length) params.set('expenseIds', expenseBatch.join(','));
          if (lineItemBatch?.length) params.set('lineItemIds', lineItemBatch.join(','));
          return authedGet<ExpenseLabelsResponse>(
            `/api/v1/expenses/orgs/${organizationId}/expenses/labels?${params.toString()}`,
          );
        }),
      );

      for (const response of responses) {
        for (const row of response.expenses) {
          result.expenses.set(row.id, row.expense_number);
        }
        for (const row of response.lineItems) {
          result.lineItems.set(row.id, row.description);
        }
      }
      return result;
    },
    enabled: !!organizationId && hasWork,
    // When there's nothing to look up (no linked expenses/line items on any
    // row), the query is disabled and would otherwise leave `data` as
    // undefined forever. Seed an empty result so consumers that gate layout
    // on `!!data` (e.g. Materials `upstreamLayoutReady` → `fillToBottom`)
    // treat the no-work case as "resolved, nothing to show".
    initialData: hasWork
      ? undefined
      : { expenses: new Map(), lineItems: new Map() },
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch line items for a specific expense plus a set of line-item ids
 * already linked to *other* assets (so the picker can grey them out).
 *
 * `currentAssetId` excludes the asset being edited from the "linked"
 * set — e.g. if the user opens the picker on a row whose expense is already
 * set, we don't want to show its own linkage as "already taken".
 */
export interface ExpenseLineItemForPicker {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number | null;
}

export const useExpenseLineItems = (
  organizationId: string | undefined,
  expenseId: string | undefined,
  currentAssetId?: string,
) => {
  return useQuery({
    queryKey: ['expenseLineItems', organizationId, expenseId, currentAssetId ?? null],
    queryFn: async (): Promise<{
      lineItems: ExpenseLineItemForPicker[];
      linkedIds: Set<string>;
    }> => {
      if (!expenseId || !organizationId) return { lineItems: [], linkedIds: new Set() };

      const params = new URLSearchParams();
      if (currentAssetId) params.set('currentAssetId', currentAssetId);
      const qs = params.toString();

      const response = await authedGet<ExpenseLineItemsResponse>(
        `/api/v1/expenses/orgs/${organizationId}/expenses/${expenseId}/line-items${qs ? `?${qs}` : ''}`,
      );

      // Array on the wire → the Set this hook's consumers expect.
      return {
        lineItems: response.lineItems,
        linkedIds: new Set(response.linkedLineItemIds),
      };
    },
    enabled: !!expenseId && !!organizationId,
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Imperative: given a freshly-created expense id, look up its first line
 * item (description + id) and the expense number. Used by flows that chain
 * off a create (e.g., materials grid committing an asset row right after
 * the inline "create expense" dialog resolves) so the UI can show a real
 * label before React Query's list refetch lands.
 *
 * Returns nulls rather than throwing on lookup failure — callers usually
 * want to proceed with partial data (the UUID-prefix label will replace
 * itself once the list refetches). Hard errors (RLS denial, expense not
 * found) still throw so they surface via Sentry in the caller.
 */
export const fetchExpenseFirstLineItem = async (
  organizationId: string,
  expenseId: string,
): Promise<ExpenseFirstLineItemResponse> => {
  // Org-scoped by the route (the pre-migration direct query had no org
  // filter — the endpoint adds one plus the membership check).
  return authedGet<ExpenseFirstLineItemResponse>(
    `/api/v1/expenses/orgs/${organizationId}/expenses/${expenseId}/first-line-item`,
  );
};

/**
 * The exact wire body `POST /api/v1/expenses/orgs/:orgId/expenses` receives
 * (`createExpenseSchema` in @releve/shared). ONE function feeds both the
 * create mutation below and the recurring-template payload
 * (`buildRecurringPayload('expense', body)` strips `expense_date`), so a key
 * added here reaches the scheduler's replayed payload the moment it lands.
 * Pure: no I/O, no defaults beyond the `?? null` coercions the schema wants.
 */
export const buildExpenseCreateBody = (data: CreateExpenseData) => ({
  business_id: data.business_id ?? null,
  person_id: data.person_id ?? null,
  custom_business_name: data.custom_business_name ?? null,
  expense_date: data.expense_date,
  description: data.description,
  payment_account_id: data.payment_account_id,
  reference: data.reference ?? null,
  project_id: data.project_id ?? null,
  fund_id: data.fund_id ?? null,
  line_items: data.line_items,
});

export type ExpenseCreateBody = ReturnType<typeof buildExpenseCreateBody>;

/**
 * Every cache a NEW expense changes. Shared by `useCreateExpense` and the
 * recurring "Run now" mutation (`invalidateGeneratedDocumentCaches`), which
 * creates expenses without going through this hook.
 */
export const invalidateExpenseCreateCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string,
) => {
  // Open History panels / the org Audit Log (docs/AUDIT.md §10a).
  invalidateAuditCaches(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['expenses', organizationId] });
  // NL WKR bridge rows are rebuilt by the write RPCs (no-op key for non-NL orgs).
  void queryClient.invalidateQueries({ queryKey: ['nl-wkr', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
  // Project spend totals (useProjectMoneySummary) read the same journals.
  invalidateProjectMoney(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['businesses'] });
  // The per-account register (bank overview) is a view of the ledger.
  void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['accountBalances', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance', organizationId] });
};

/**
 * Plain create call — no toast, no cache invalidation. `useCreateExpense`
 * wraps it with both (module-private helper; creation is modal-only).
 */
const postCreateExpense = async (
  organizationId: string,
  data: CreateExpenseData,
): Promise<ExpenseMutationResult> => {
  // MED-1 (SCRUM-184): route through the Hono backend (HMAC) instead of a
  // direct supabase.rpc — create_expense_with_line_items writes the ledger.
  const result = await authedPost<ExpenseMutationResult>(
    `/api/v1/expenses/orgs/${organizationId}/expenses`,
    buildExpenseCreateBody(data),
  );

  if (!result?.success) {
    throw new Error(result?.error || 'Failed to create expense');
  }

  return result;
};

export const useCreateExpense = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({ organizationId, data }: { organizationId: string; data: CreateExpenseData }) =>
      postCreateExpense(organizationId, data),
    onSuccess: (_, { organizationId, data }) => {
      invalidateExpenseCreateCaches(queryClient, organizationId);
      trackEvent('expense_created', { line_count: data.line_items.length });

      toast.successTemplates.created('Expense');
    },
    onError: (error) => {
      // A coded tax-rate refusal names what to fix; anything else stays generic.
      if (toastTaxRateRefusal(error, t)) return;
      toast.errorTemplates.create('expense');
    },
  });
};

export const useExpenseById = (organizationId?: string, expenseId?: string) => {
  return useQuery({
    queryKey: ['expense', organizationId, expenseId],
    queryFn: async () => {
      if (!organizationId || !expenseId) return null;

      // Detail read (no project embeds, line items merged server-side).
      const result = await authedGet<ExpenseDetailResponse>(
        `/api/v1/expenses/orgs/${organizationId}/expenses/${expenseId}`,
      );
      return result.expense as Expense;
    },
    enabled: !!organizationId && !!expenseId,
  });
};

interface UpdateExpenseData {
  expense_id: string;
  business_id?: string;
  person_id?: string;
  custom_business_name?: string;
  expense_date: string;
  description: string;
  payment_account_id: string;
  reference?: string;
  project_id?: string;
  fund_id?: string;
  line_items: Array<{
    expense_account_id: string;
    description: string;
    quantity: number;
    unit_price: number;
    tax_rate: number;
    tax_rate_id?: string;
    sort_order: number;
    tax_treatment?: string;
    tax_inclusive?: boolean;
    exemption_reason?: string;
    exemption_reason_other?: string;
    /** NL WKR classification (jurisdiction-gated in the UI). */
    wkr_category?: NlWkrCategory;
  }>;
}

/**
 * Cache fan-out shared by the full and the header-only expense updates. Both
 * rewrite what the expense index, the journal index, the register and the
 * Impact index display, so a key dropped from one and not the other shows up
 * as a stale row after the other kind of save.
 */
export function invalidateExpenseUpdateCaches(queryClient: QueryClient, organizationId: string) {
  // Open History panels / the org Audit Log (docs/AUDIT.md §10a).
  invalidateAuditCaches(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['expenses', organizationId] });
  // NL WKR bridge rows are rebuilt by the write RPCs (no-op key for non-NL orgs).
  void queryClient.invalidateQueries({ queryKey: ['nl-wkr', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['expense'] });
  void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
  // Project spend totals (useProjectMoneySummary) read the same journals.
  invalidateProjectMoney(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['businesses'] });
  // The per-account register (bank overview) is a view of the ledger.
  void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['accountBalances', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance', organizationId] });
  // Account/amount changes can shift the linked transaction's calculated
  // emissions; refresh dashboards that read from the carbon caches.
  // …and the Impact slim index shows the journal's description/date/project
  // on its carbon rows (useSustainabilityActivityIndex contract).
  invalidateCarbonCaches(queryClient, organizationId);
}

/**
 * The update-failure toast, shared by the full and the header-only updates.
 *
 * A declared expense is read-only in full (update_expense_with_line_items
 * deletes and re-posts every line, so even a description fix is refused).
 * Name the filed period instead of the generic template; the correction is a
 * new journal, not an edit. See docs/TAX.md "Edit guard".
 */
export function toastExpenseUpdateError(error: unknown, t: TFunction, language: string) {
  const refusal = vatDeclaredOf(error);
  if (refusal) {
    toast.error(vatDeclaredMessage(refusal, t, language));
    return;
  }
  if (toastTaxRateRefusal(error, t)) return;
  toast.errorTemplates.update('expense');
}

/**
 * The user-facing text for a declared-VAT refusal on an expense — names the
 * filed period in the user's language (date-fns `PP` = localized medium date,
 * so the NL/FR sentence does not carry an English month). Shared by the update
 * toasts and the bulk delete.
 */
export function vatDeclaredMessage(
  refusal: { period_start?: string | null; period_end?: string | null },
  t: TFunction,
  language: string,
): string {
  return formatVatDeclaredMessage('expenses.errors.vatDeclared', refusal, t, language);
}

export const useUpdateExpense = () => {
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();

  return useMutation({
    meta: VAT_DECLARED_MUTATION_META,
    mutationFn: async ({ organizationId, data }: { organizationId: string; data: UpdateExpenseData }) => {
      // MED-1 (SCRUM-184): route through the Hono backend — update_expense_with_line_items writes the ledger.
      const result = await authedPatch<ExpenseMutationResult>(
        `/api/v1/expenses/orgs/${organizationId}/expenses/${data.expense_id}`,
        {
          business_id: data.business_id ?? null,
          person_id: data.person_id ?? null,
          custom_business_name: data.custom_business_name ?? null,
          expense_date: data.expense_date,
          description: data.description,
          payment_account_id: data.payment_account_id,
          reference: data.reference ?? null,
          project_id: data.project_id ?? null,
          fund_id: data.fund_id ?? null,
          line_items: data.line_items,
        },
      );

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to update expense');
      }

      return result;
    },
    onSuccess: (_, { organizationId }) => {
      invalidateExpenseUpdateCaches(queryClient, organizationId);
      toast.successTemplates.updated('Expense');
    },
    onError: (error) => toastExpenseUpdateError(error, t, i18n.language),
  });
};

/**
 * Header-only save for a **declared** expense (`update_expense_header`, plan
 * "Declared documents in the UI"). The full sibling above deletes and re-posts
 * every ledger line, which the declared-VAT guard refuses once the period is
 * filed; this route touches only what stays editable — payee, description,
 * project, fund, reference — and no line, amount, date or payment account.
 * The form picks this hook when the loaded detail carries a
 * `vat_settlement_id` (`declaredSettlementIdOf`) and the full one otherwise.
 * The expenses grid's staged Payee edit also saves through it, for ANY
 * expense (declared or not): the payee is a header field, so no line needs
 * re-posting. The grid round-trips the other header fields from the index
 * row and passes `silent` (it invalidates once and shows one summary).
 *
 * Same cache fan-out as the full update: the header fields it writes are the
 * ones the expense index, the journal index and the Impact index display, and
 * the fund move is an in-place `ledger_entries.fund_id` update the register
 * and balances read.
 */
export interface UpdateExpenseHeaderData {
  business_id?: string | null;
  person_id?: string | null;
  custom_business_name?: string | null;
  description: string;
  project_id?: string | null;
  fund_id?: string | null;
  reference?: string | null;
}

export const useUpdateExpenseHeader = () => {
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();

  return useMutation({
    // Still guarded: a filing that lands after the form loaded turns this into
    // a refused edit only if a frozen column moved, but the code stays declared
    // so the global handler keeps the rule out of Sentry either way.
    meta: VAT_DECLARED_MUTATION_META,
    mutationFn: async ({
      organizationId,
      expenseId,
      ...header
    }: {
      organizationId: string;
      expenseId: string;
      /** Batch mode: skip this call's cache fan-out and toasts (the caller does both). */
      silent?: boolean;
    } & UpdateExpenseHeaderData) => {
      const result = await authedPatch<ExpenseMutationResult>(
        `/api/v1/expenses/orgs/${organizationId}/expenses/${expenseId}/header`,
        {
          business_id: header.business_id ?? null,
          person_id: header.person_id ?? null,
          custom_business_name: header.custom_business_name ?? null,
          description: header.description,
          project_id: header.project_id ?? null,
          fund_id: header.fund_id ?? null,
          reference: header.reference ?? null,
        },
      );

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to update expense');
      }

      return result;
    },
    onSuccess: (_, { organizationId, silent }) => {
      if (silent) return;
      invalidateExpenseUpdateCaches(queryClient, organizationId);
      toast.successTemplates.updated('Expense');
    },
    onError: (error, { silent }) => {
      if (silent) return;
      toastExpenseUpdateError(error, t, i18n.language);
    },
  });
};

/**
 * Cache fan-out after one or more expense deletes — shared by the single
 * delete and the bulk delete (which invalidates ONCE per run).
 */
export function invalidateExpenseDeleteCaches(queryClient: QueryClient, organizationId: string) {
  // Open History panels / the org Audit Log (docs/AUDIT.md §10a).
  invalidateAuditCaches(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['expenses', organizationId] });
  // NL WKR bridge rows are rebuilt by the write RPCs (no-op key for non-NL orgs).
  void queryClient.invalidateQueries({ queryKey: ['nl-wkr', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
  // Project spend totals (useProjectMoneySummary) read the same journals.
  invalidateProjectMoney(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['businesses'] });
  // The per-account register (bank overview) is a view of the ledger.
  void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['accountBalances', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance', organizationId] });
  // Deleting the expense's journal cascade-deletes its carbon rows out
  // of the Impact slim index (useSustainabilityActivityIndex contract).
  invalidateCarbonCaches(queryClient, organizationId);
}

export const useDeleteExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // The declared-VAT refusal's human text already reaches the user via
    // `error.message` below; meta only keeps Sentry quiet (VAT_DECLARED_MUTATION_META).
    meta: VAT_DECLARED_MUTATION_META,
    mutationFn: async ({ organizationId, expenseId }: { organizationId: string; expenseId: string }) => {
      // MED-1 (SCRUM-184): route through the Hono backend — delete_expense reverses the ledger.
      const result = await authedDelete<{ success: boolean; error?: string; message?: string }>(
        `/api/v1/expenses/orgs/${organizationId}/expenses/${expenseId}`,
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete expense');
      }

      return result;
    },
    onSuccess: (_, { organizationId }) => {
      invalidateExpenseDeleteCaches(queryClient, organizationId);

      toast.successTemplates.deleted('Expense');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete expense');
    },
  });
};

// ---------- Bulk delete (the expenses grid's bulk actions) ----------

/** Transport codes the bulk delete stamps on a row itself — never an RPC outcome. */
export const EXPENSE_BATCH_REQUEST_FAILED = BULK_DELETE_REQUEST_FAILED;
export const EXPENSE_BATCH_NOT_ATTEMPTED = BULK_DELETE_NOT_ATTEMPTED;

/** One selected row — only what the pre-skip needs. */
export interface BulkDeleteExpenseRow {
  id: string;
  /** Declared rows are pre-skipped (UX only — the server trigger still guards). */
  vat_declared_settlement_id: string | null;
}

export interface BulkDeleteExpensesInput {
  organizationId: string;
  expenses: ReadonlyArray<BulkDeleteExpenseRow>;
}

export interface BulkDeleteExpensesOutcome extends BulkDeleteOutcome<ExpenseBatchDeleteResult> {
  /** Pre-skipped (declared VAT) — never sent. */
  skipped: string[];
}

export type { RunBulkDeleteOptions };

const EXPENSE_BULK_DELETE: BulkDeleteConfig = {
  feature: 'expenses',
  sentryTags: { expense_action: 'bulk-delete' },
  route: {
    path: (organizationId) => `/api/v1/expenses/orgs/${organizationId}/expenses/batch-delete`,
    idsKey: 'expense_ids',
    idField: 'expense_id',
    maxIds: EXPENSE_BATCH_MAX_IDS,
  },
  invalidate: invalidateExpenseDeleteCaches,
  i18n: {
    done: 'expenses.bulkDelete.toastDone',
    rowFailed: 'expenses.bulkDelete.rowFailed',
    outcomeUnknown: 'expenses.bulkDelete.outcomeUnknown',
    vatDeclared: 'expenses.errors.vatDeclared',
  },
};

/**
 * Bulk delete for the expenses grid: POST
 * /api/v1/expenses/orgs/:orgId/expenses/batch-delete, which runs the SAME
 * `delete_expense` path as the single DELETE route, one id at a time — a thin
 * wrapper over the shared `useBulkDelete` (queue, capture, toasts, one
 * invalidation).
 *
 * Declared rows (`vat_declared_settlement_id`) are pre-skipped here — UX only,
 * the declared-VAT trigger still guards every row the server touches — and
 * reported with one info toast; progress counts them as done.
 */
export function useBulkDeleteExpenses() {
  const { t } = useTranslation();
  const { run: runShared, isRunning } = useBulkDelete<ExpenseBatchDeleteResult>(EXPENSE_BULK_DELETE);

  const run = useCallback(
    async (
      { organizationId, expenses }: BulkDeleteExpensesInput,
      { onProgress }: RunBulkDeleteOptions = {},
    ): Promise<BulkDeleteExpensesOutcome> => {
      const skipped = expenses.filter((e) => e.vat_declared_settlement_id).map((e) => e.id);
      const ids = expenses.filter((e) => !e.vat_declared_settlement_id).map((e) => e.id);

      const outcome = await runShared(organizationId, ids, {
        onProgress: (done, total) => onProgress?.(skipped.length + done, skipped.length + total),
      });

      if (skipped.length > 0) {
        toast.info(t('expenses.bulkDelete.toastSkippedDeclared', { count: skipped.length }));
      }
      return { ...outcome, skipped };
    },
    [runShared, t],
  );

  return { run, isRunning };
}
